import {
  addDoc,
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import {
  auth,
  dbAccounts,
  dbProduction,
} from "../../../../backend/firebaseConfig";
import { isAmlGuidelineCharge } from "../constants/guidelineCharges";
import { filterManualChargesForAccess } from "../utils/adviserChargeAccess";
import {
  assertChargeDatesAreOpen,
  canOverrideClosedChargeMonths,
} from "../utils/closedChargeMonths";

const MANUAL_CHARGES_COLLECTION = "MonthlyManualCharges";
const charges = collection(dbAccounts, MANUAL_CHARGES_COLLECTION);
const chargeRef = (chargeId) =>
  doc(dbAccounts, MANUAL_CHARGES_COLLECTION, chargeId);
const editLogsRef = (chargeId) => collection(chargeRef(chargeId), "Edit Logs");

const currentUserCanOverrideClosedChargeMonths = async (userPermissions = []) => {
  if (canOverrideClosedChargeMonths(userPermissions)) return true;

  const email = String(auth.currentUser?.email || "").trim().toLowerCase();
  if (!email) return false;

  try {
    const snapshot = await getDoc(
      doc(dbProduction, "Authorized Members", "Staff", "Data", email),
    );
    return canOverrideClosedChargeMonths(snapshot.data()?.permissions);
  } catch {
    return false;
  }
};

const assertManualChargeDatesAreOpen = async (dates, userPermissions = []) => {
  if (await currentUserCanOverrideClosedChargeMonths(userPermissions)) return;
  await assertChargeDatesAreOpen(dates);
};

const monthOf = (date) => String(date || "").slice(0, 7);
const toIsoDate = (date) => {
  const parsed = new Date(`${date}T00:00:00.000Z`);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString();
};

export const subscribeMonthlyManualCharges = (onData, onError, access) =>
  onSnapshot(
    charges,
    (snapshot) => {
      const records = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
      onData(access ? filterManualChargesForAccess(records, access) : records);
    },
    onError,
  );

export const subscribeMonthlyManualChargeEditLogs = (
  chargeId,
  onData,
  onError,
) =>
  onSnapshot(
    editLogsRef(chargeId),
    (snapshot) =>
      onData(
        snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort(
            (left, right) =>
              (right.updatedAt?.toMillis?.() || 0) -
              (left.updatedAt?.toMillis?.() || 0),
          ),
      ),
    onError,
  );

export const createOneTimeManualCharge = async ({
  adviserEmail,
  adviserName,
  adviserId = "",
  brandName = "",
  chargeType,
  chargeTypeId = "",
  description,
  client = "",
  checkType = "",
  amount,
  date,
  notes = "",
  userPermissions = [],
}) => {
  const normalisedEmail = String(adviserEmail || "").trim().toLowerCase();
  const amountCharged = Number(amount);
  const isAmlCharge = isAmlGuidelineCharge({ chargeTypeId, chargeType });
  if (!normalisedEmail) throw new Error("An adviser email is required.");
  if (!String(chargeType || "").trim()) throw new Error("A charge type is required.");
  if (isAmlCharge && !String(client).trim()) {
    throw new Error("A client name is required for AML charges.");
  }
  if (isAmlCharge && !String(description).trim()) {
    throw new Error("A charge description is required for AML charges.");
  }
  if (chargeTypeId === "smartsearch" && !String(checkType).trim()) {
    throw new Error("A check type is required for Smart Search.");
  }
  if (!date) throw new Error("A charge date is required.");
  if (!Number.isFinite(amountCharged) || amountCharged < 0) {
    throw new Error("Enter a valid charge amount.");
  }

  const user = auth.currentUser;
  const isoDate = toIsoDate(date);
  if (!isoDate) throw new Error("Enter a valid charge date.");
  await assertManualChargeDatesAreOpen([isoDate], userPermissions);
  const chargeDocument = await addDoc(charges, {
    recordType: "manual_charge",
    billingType: "one_time",
    status: "active",
    adviserEmail: normalisedEmail,
    adviserName: String(adviserName || "").trim(),
    adviserId: String(adviserId || "").trim(),
    brandName: String(brandName || "").trim(),
    chargeType: String(chargeType).trim(),
    chargeTypeId: String(chargeTypeId).trim(),
    description: String(description).trim(),
    client: String(client).trim(),
    checkType: String(checkType).trim(),
    amountCharged,
    date: isoDate,
    billingMonth: monthOf(isoDate),
    notes: String(notes || "").trim(),
    createdAt: serverTimestamp(),
    createdBy: {
      uid: user?.uid || "",
      email: user?.email || "",
      name: user?.displayName || user?.email || "",
    },
  });
  await addDoc(collection(chargeDocument, "Edit Logs"), {
    action: "created",
    changes: [],
    updatedAt: serverTimestamp(),
    updatedByName: user?.displayName || user?.email || "",
    updatedBy: user?.email || "",
  });
};

export const updateOneTimeManualCharge = async ({
  chargeId,
  previousCharge,
  adviserEmail,
  adviserName,
  adviserId = "",
  brandName = "",
  chargeType,
  chargeTypeId = "",
  description,
  client = "",
  checkType = "",
  amount,
  date,
  notes = "",
  userPermissions = [],
}) => {
  const normalisedEmail = String(adviserEmail || "").trim().toLowerCase();
  const amountCharged = Number(amount);
  const isoDate = toIsoDate(date);
  const isAmlCharge = isAmlGuidelineCharge({ chargeTypeId, chargeType });
  if (!chargeId) throw new Error("A charge ID is required.");
  if (!normalisedEmail) throw new Error("An adviser email is required.");
  if (!String(chargeType || "").trim()) throw new Error("A charge type is required.");
  if (isAmlCharge && !String(client).trim()) {
    throw new Error("A client name is required for AML charges.");
  }
  if (isAmlCharge && !String(description).trim()) {
    throw new Error("A charge description is required for AML charges.");
  }
  if (chargeTypeId === "smartsearch" && !String(checkType).trim()) {
    throw new Error("A check type is required for Smart Search.");
  }
  if (!isoDate) throw new Error("Enter a valid charge date.");
  if (!Number.isFinite(amountCharged) || amountCharged < 0) {
    throw new Error("Enter a valid charge amount.");
  }

  const user = auth.currentUser;
  const values = {
    billingType: "one_time",
    adviserEmail: normalisedEmail,
    adviserName: String(adviserName || "").trim(),
    adviserId: String(adviserId || "").trim(),
    brandName: String(brandName || "").trim(),
    chargeType: String(chargeType).trim(),
    chargeTypeId: String(chargeTypeId).trim(),
    description: String(description).trim(),
    client: String(client).trim(),
    checkType: String(checkType).trim(),
    amountCharged,
    date: isoDate,
    billingMonth: monthOf(isoDate),
    notes: String(notes || "").trim(),
  };
  const previous = {
    ...previousCharge,
    chargeType: previousCharge?.manualChargeType || previousCharge?.chargeType,
  };
  const changes = Object.entries(values)
    .filter(([field, value]) => String(previous[field] ?? "") !== String(value ?? ""))
    .map(([field, newValue]) => ({
      fieldPath: field,
      previousValue: previous[field] ?? "",
      newValue,
    }));
  if (!changes.length) return;
  await assertManualChargeDatesAreOpen(
    [previousCharge?.date, isoDate],
    userPermissions,
  );

  const batch = writeBatch(dbAccounts);
  const target = chargeRef(chargeId);
  batch.update(target, {
    ...values,
    chargeFrom: deleteField(),
    chargeTo: deleteField(),
    repaymentInstalments: deleteField(),
    annualInterestRatePa: deleteField(),
    annualBoeBaseRatePa: deleteField(),
    bankRateAsOf: deleteField(),
    monthlyInterestRatePm: deleteField(),
    monthlyInterestAmount: deleteField(),
    monthlyRepaymentAmount: deleteField(),
    currentMonthlyRepaymentAmount: deleteField(),
    currentRepaymentInstalment: deleteField(),
    currentBalanceRemaining: deleteField(),
    totalRepaymentAmount: deleteField(),
    totalInterest: deleteField(),
    repaymentSchedule: deleteField(),
    updatedAt: serverTimestamp(),
    updatedBy: {
      uid: user?.uid || "",
      email: user?.email || "",
      name: user?.displayName || user?.email || "",
    },
  });
  batch.set(doc(editLogsRef(chargeId)), {
    action: "updated",
    changes,
    updatedAt: serverTimestamp(),
    updatedByName: user?.displayName || user?.email || "",
    updatedBy: user?.email || "",
  });
  await batch.commit();
};

const recurringValues = ({
  adviserEmail,
  adviserName,
  adviserId = "",
  brandName = "",
  chargeType,
  chargeTypeId = "",
  description = "",
  totalAmount,
  date,
  notes = "",
  chargeFrom,
  chargeTo,
  repaymentInstalments,
  annualInterestRatePa,
  annualBoeBaseRatePa,
  bankRateAsOf = "",
  monthlyInterestRatePm,
  monthlyInterestAmount,
  monthlyRepaymentAmount,
  currentMonthlyRepaymentAmount,
  currentRepaymentInstalment,
  currentBalanceRemaining,
  totalRepaymentAmount,
  totalInterest,
  repaymentSchedule = [],
}) => {
  const isLoanRepayment = chargeTypeId === "loan-repayment";
  return {
    recordType: "manual_charge",
    billingType: "recurring",
    status: "active",
    adviserEmail: String(adviserEmail || "").trim().toLowerCase(),
    adviserName: String(adviserName || "").trim(),
    adviserId: String(adviserId || "").trim(),
    brandName: String(brandName || "").trim(),
    chargeType: String(chargeType || "").trim(),
    chargeTypeId: String(chargeTypeId || "").trim(),
    description: String(description || "").trim(),
    amountCharged: Number(totalAmount),
    date: toIsoDate(date),
    billingMonth: monthOf(date),
    notes: String(notes || "").trim(),
    chargeFrom: toIsoDate(chargeFrom),
    chargeTo: toIsoDate(chargeTo),
    repaymentInstalments: isLoanRepayment ? Number(repaymentInstalments) : null,
    annualInterestRatePa: isLoanRepayment ? Number(annualInterestRatePa) : null,
    annualBoeBaseRatePa: isLoanRepayment ? Number(annualBoeBaseRatePa) : null,
    bankRateAsOf: isLoanRepayment ? bankRateAsOf : "",
    monthlyInterestRatePm: isLoanRepayment ? Number(monthlyInterestRatePm) : null,
    monthlyInterestAmount: isLoanRepayment ? Number(monthlyInterestAmount) : null,
    monthlyRepaymentAmount: isLoanRepayment
      ? Number(monthlyRepaymentAmount)
      : Number(totalAmount),
    currentMonthlyRepaymentAmount: isLoanRepayment
      ? Number(currentMonthlyRepaymentAmount)
      : null,
    currentRepaymentInstalment: isLoanRepayment
      ? String(currentRepaymentInstalment || "")
      : "",
    currentBalanceRemaining: isLoanRepayment
      ? Number(currentBalanceRemaining)
      : null,
    totalRepaymentAmount: isLoanRepayment ? Number(totalRepaymentAmount) : null,
    totalInterest: isLoanRepayment ? Number(totalInterest) : null,
    repaymentSchedule: isLoanRepayment ? repaymentSchedule : [],
  };
};

const validateRecurringValues = (values) => {
  const isLoanRepayment = values.chargeTypeId === "loan-repayment";
  if (!values.adviserEmail) throw new Error("An adviser email is required.");
  if (!values.chargeType) throw new Error("A charge type is required.");
  if (!values.date || !values.chargeFrom) {
    throw new Error("Enter a valid date and charge start date.");
  }
  if (isLoanRepayment && !values.chargeTo) {
    throw new Error("Enter a valid charge end date for the loan repayment.");
  }
  if (
    values.chargeTo &&
    new Date(values.chargeTo).getTime() < new Date(values.chargeFrom).getTime()
  ) {
    throw new Error("The charge end date must be after the charge start date.");
  }
  if (!Number.isFinite(values.amountCharged) || values.amountCharged <= 0) {
    throw new Error("Enter a valid total amount.");
  }
  if (
    isLoanRepayment &&
    (!Number.isFinite(values.monthlyRepaymentAmount) || values.monthlyRepaymentAmount <= 0)
  ) {
    throw new Error("Enter a valid monthly repayment amount.");
  }
};

export const createRecurringManualCharge = async (input) => {
  const values = recurringValues(input);
  validateRecurringValues(values);
  await assertManualChargeDatesAreOpen(
    [values.date, values.chargeFrom],
    input.userPermissions,
  );
  const user = auth.currentUser;
  const chargeDocument = await addDoc(charges, {
    ...values,
    createdAt: serverTimestamp(),
    createdBy: {
      uid: user?.uid || "",
      email: user?.email || "",
      name: user?.displayName || user?.email || "",
    },
  });
  await addDoc(collection(chargeDocument, "Edit Logs"), {
    action: "created",
    changes: [],
    updatedAt: serverTimestamp(),
    updatedByName: user?.displayName || user?.email || "",
    updatedBy: user?.email || "",
  });
};

export const updateRecurringManualCharge = async ({ chargeId, previousCharge, ...input }) => {
  if (!chargeId) throw new Error("A charge ID is required.");
  const values = recurringValues(input);
  validateRecurringValues(values);
  const changes = Object.entries(values)
    .filter(([field]) => field !== "repaymentSchedule")
    .filter(([field, value]) => String(previousCharge?.[field] ?? "") !== String(value ?? ""))
    .map(([field, newValue]) => ({
      fieldPath: field,
      previousValue: previousCharge?.[field] ?? "",
      newValue,
    }));
  if (!changes.length) return;
  await assertManualChargeDatesAreOpen([
    previousCharge?.date,
    previousCharge?.chargeFrom,
    values.date,
    values.chargeFrom,
  ], input.userPermissions);
  const user = auth.currentUser;
  const batch = writeBatch(dbAccounts);
  batch.update(chargeRef(chargeId), {
    ...values,
    updatedAt: serverTimestamp(),
    updatedBy: {
      uid: user?.uid || "",
      email: user?.email || "",
      name: user?.displayName || user?.email || "",
    },
  });
  if (changes.length) {
    batch.set(doc(editLogsRef(chargeId)), {
      action: "updated",
      changes,
      updatedAt: serverTimestamp(),
      updatedByName: user?.displayName || user?.email || "",
      updatedBy: user?.email || "",
    });
  }
  await batch.commit();
};

export const deleteManualCharge = async (chargeId, userPermissions = []) => {
  if (!chargeId) throw new Error("A charge ID is required.");

  const batch = writeBatch(dbAccounts);
  const target = chargeRef(chargeId);
  const chargeSnapshot = await getDoc(target);
  if (!chargeSnapshot.exists()) throw new Error("Charge not found.");
  const charge = chargeSnapshot.data();
  await assertManualChargeDatesAreOpen(
    [charge.date, charge.chargeFrom],
    userPermissions,
  );
  const editLogs = await getDocs(editLogsRef(chargeId));
  editLogs.docs.forEach((log) => batch.delete(log.ref));
  batch.delete(target);
  await batch.commit();
};
